"use client";
import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { Search, X } from "lucide-react";
import { getFirestore } from "firebase/firestore";
import {
  collection,
  query,
  getDocs,
  orderBy,
} from "firebase/firestore";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { app } from "../../app/firebase";

const firestore = getFirestore(app);

const SearchBar = ({ isMobile }) => {
  const router = useRouter();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  
  // Animated placeholder states
  const [placeholderText, setPlaceholderText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPlaceholder, setCurrentPlaceholder] = useState('Search our collection...');
  
  // Animation timing configuration
  const typingSpeed = 150;
  const deletingSpeed = 100;
  const delayBeforeDeleting = 2000;
  const delayBeforeTyping = 1000;

  // Get random placeholder text
  const getRandomPlaceholder = () => {
    if (products.length === 0 && categories.size === 0) {
      return "Search our collection...";
    }

    const placeholderOptions = [
      ...Array.from(categories).map(category => `Search for "${category}"...`),
      ...products
        .slice(0, 5) // Limit to first 5 products
        .map(product => `Find "${product.productName}"...`),
      "Search our collection...",
      "Discover unique items...",
      "Find the perfect gift..."
    ];
    
    let newPlaceholder;
    do {
      newPlaceholder = placeholderOptions[Math.floor(Math.random() * placeholderOptions.length)];
    } while (newPlaceholder === currentPlaceholder && placeholderOptions.length > 1);
    
    return newPlaceholder;
  };

  // Fetch products from Firestore
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsCollection = collection(firestore, "products");
        const querySnapshot = await getDocs(
          query(productsCollection, orderBy("createdAt", "desc"))
        );
        const productsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(productsData);

        // Extract unique categories
        const categoriesSet = new Set();
        productsData.forEach((product) => {
          if (product.Category) {
            categoriesSet.add(product.Category);
          }
        });
        setCategories(categoriesSet);
      } catch (error) {
        console.error("Error fetching products: ", error);
      }
    };
    fetchProducts();
  }, []);

  // Handle typing animation
  useEffect(() => {
    let timeout;

    if (!isDeleting && currentPlaceholder) {
      if (placeholderText.length < currentPlaceholder.length) {
        timeout = setTimeout(() => {
          setPlaceholderText(currentPlaceholder.slice(0, placeholderText.length + 1));
        }, typingSpeed);
      } else {
        timeout = setTimeout(() => {
          setIsDeleting(true);
        }, delayBeforeDeleting);
      }
    } else if (isDeleting) {
      if (placeholderText.length === 0) {
        setIsDeleting(false);
        setCurrentPlaceholder(getRandomPlaceholder());
      } else {
        timeout = setTimeout(() => {
          setPlaceholderText(currentPlaceholder.slice(0, placeholderText.length - 1));
        }, deletingSpeed);
      }
    }

    return () => clearTimeout(timeout);
  }, [placeholderText, isDeleting, currentPlaceholder]);

  // Filter search results
  useEffect(() => {
    if (searchTerm) {
      const results = products.filter(
        (product) =>
          (product.productName &&
            product.productName
              .toLowerCase()
              .includes(searchTerm.toLowerCase())) ||
          (product.Category &&
            product.Category.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setSearchResults(results);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchTerm, products]);

  // Handle click outside search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = (e) => {
    if (e.key === "Enter" && searchTerm) {
      const matchingCategory = Array.from(categories).find((category) =>
        category.toLowerCase().includes(searchTerm.toLowerCase())
      );

      if (matchingCategory) {
        router.push(`/products/${matchingCategory.toLowerCase().replace(/\s+/g, "-")}`);
      } else {
        router.push(`/shop?search=${searchTerm}`);
      }
      setShowResults(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setShowResults(false);
  };

  const searchContainerClasses = isMobile
    ? "w-full"
    : "hidden md:block flex-1 max-w-lg mx-8";

  return (
    <div className={searchContainerClasses} ref={searchRef}>
      <div className="relative group">
        <input
          type="text"
          placeholder={placeholderText}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={handleSearch}
          className="w-full pl-4 pr-10 py-2 border-2 border-red-200 rounded-full focus:outline-none focus:border-red-500 bg-gray-50 transition-all duration-300 group-hover:border-red-400"
          aria-label="Search products"
        />
        <Search className="absolute right-3 top-2.5 text-gray-400 group-hover:text-red-500 transition-colors duration-300 h-5 w-5" />
        
        {searchTerm && (
          <button
            onClick={clearSearch}
            className="absolute right-10 top-2.5 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        {/* Search Results Dropdown */}
        {showResults && searchResults.length > 0 && (
          <div className="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-y-auto">
            {searchResults.map((product) => (
              <Link
                href={`/product-detail/${product.productName.replace(/\s+/g, '-')}`}
                key={product.id}
                className="flex items-center p-3 hover:bg-gray-100 transition duration-150 ease-in-out"
                onClick={() => {
                  setShowResults(false);
                  setSearchTerm("");
                }}
              >
                <div className="flex-shrink-0 w-12 h-12 mr-4">
                  {product.productImages && product.productImages.length > 0 ? (
                    <Image
                      src={product.productImages[0]}
                      alt={product.productName}
                      width={48}
                      height={48}
                      className="object-cover rounded"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                      <span className="text-gray-500 text-xs">No image</span>
                    </div>
                  )}
                </div>
                <div className="flex-grow">
                  <p className="text-sm font-medium text-gray-900">
                    {product.productName}
                  </p>
                  <p className="text-xs text-gray-500">{product.Category}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBar;